/**
 * A BIZONYLAT TENSOFT-ÁLLAPOTA — betöltés és a kétes kötés döntése (#54, T19).
 *
 * ⚠️ A HOROG AZ `App`-BAN ÉL, nem a panelben. A `1`–`9` és a `0` a közös
 * rövidítés-regiszterbe kerül (`useShortcuts.ts`), és a regiszter az `App`-é:
 * egy panelben bejegyzett második regiszter a súgót ketté hasítaná.
 *
 * A DÖNTÉS NEM OPTIMISTA. A sor-mód `Enter`-e azért optimista, mert naponta
 * több százszor üt; a kétes kötés ritka, és a rossz kötés csendes hiba —
 * itt a szerver válasza után mutatjuk az új állapotot.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import type { Shortcut } from '@/hooks/useShortcuts';
import { ApiError, api } from '@/lib/api-client';
import { type Chooser, chooserOf, KEYED_CANDIDATES } from './chooser';
import type { TensoftBookingView } from './types';

export type TensoftStatus = 'loading' | 'failed' | 'ready';
export type TensoftChoiceError = 'failed' | 'conflict' | null;

export interface TensoftBookingState {
  status: TensoftStatus;
  view: TensoftBookingView | null;
  /** A választó, ha most dönteni kell vagy lehet — különben `null`. */
  chooser: Chooser | null;
  /** A könyvelő egy meglévő döntést nyitott újra. */
  rechoosing: boolean;
  startRechoose: () => void;
  cancelRechoose: () => void;
  /** A döntés: számla azonosítója, vagy `null` = „Egyik sem". */
  choose: (ownerId: string | null) => void;
  saving: boolean;
  error: TensoftChoiceError;
  reload: () => void;
  shortcuts: Shortcut[];
  /**
   * A MOST ELDÖNTÖTT BIZONYLATOK — a lista „kétes kötés" jelvénye ezeken már
   * nem áll. A munkalista sor-módban nem töltődik újra minden döntés után
   * (az egy hálózati kör volna leütésenként), tehát a jelvényt helyben kell
   * levenni; a következő újratöltéskor a szerver jelzése az irányadó.
   *
   * Az érték a döntés IDEJE (`Date.now()`): a lista újratöltése csak a
   * betöltés KEZDETE előtti döntéseket felejti el — egy már úton lévő, régebbi
   * lista különben visszahozná a most levett jelvényt (code review, 10-05).
   */
  decided: ReadonlyMap<string, number>;
  /** A `since` előtt hozott döntéseket elfelejti: azokról a szerver már tud. */
  pruneDecided: (since: number) => void;
}

/**
 * `active`: a munkalista látszik-e. A hiánylistán a horog nem kér semmit —
 * a kurzor ott is ott marad a munkalista egy bizonylatán, de a panel nem látszik.
 */
export function useTensoftBooking(focusedId: string | null, active: boolean): TensoftBookingState {
  const documentId = active ? focusedId : null;
  const { signOut } = useAuth();
  const [view, setView] = useState<TensoftBookingView | null>(null);
  const [status, setStatus] = useState<TensoftStatus>('loading');
  const [rechoosing, setRechoosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<TensoftChoiceError>(null);
  const [decided, setDecided] = useState<ReadonlyMap<string, number>>(() => new Map());
  const markDecided = useCallback((id: string) => {
    setDecided((previous) => new Map(previous).set(id, Date.now()));
  }, []);

  /**
   * MELYIK BIZONYLATÉ A VÁLASZ. Sor-módban a kurzor másodpercenként léphet
   * tovább; egy lassú válasz az ELŐZŐ bizonylatról nem írhatja felül a
   * mostanit — a könyvelő más papír kontírját látná a bizonylat mellett.
   */
  const current = useRef(documentId);
  current.current = documentId;

  const load = useCallback(
    (id: string, keepError = false) => {
      setStatus('loading');
      void api<TensoftBookingView>(`/documents/${id}/tensoft-booking`)
        .then((loaded) => {
          if (current.current !== id) return;
          setView(loaded);
          setStatus('ready');
          // A DÖNTÉS UTÁNI ÚJRATÖLTÉSIG A VÁLASZTÓ NÉMA. Addig a régi
          // `NEEDS_CHOICE` állna a képernyőn élő `1`–`9`/`0`-val, és egy gyors
          // második leütés második döntést küldene (review, 10-05).
          setSaving(false);
          if (!keepError) setError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            signOut('expired');
            return;
          }
          if (current.current !== id) return;
          setSaving(false);
          setStatus('failed');
        });
    },
    [signOut]
  );

  useEffect(() => {
    setView(null);
    setRechoosing(false);
    setSaving(false);
    setError(null);
    if (documentId === null) {
      setStatus('ready');
      return;
    }
    load(documentId);
  }, [documentId, load]);

  // MEMOIZÁLVA: a rövidítések erre épülnek, és a regiszter minden új
  // tömbnél újra feliratkozik.
  const chooser = useMemo(() => chooserOf(view, rechoosing), [view, rechoosing]);

  const choose = useCallback(
    (ownerId: string | null) => {
      if (documentId === null || chooser === null || saving) return;
      const id = documentId;
      setSaving(true);
      setError(null);
      // AZ „EGYIK SEM" IS A KULCSRA SZÓL — a kulcsot a bizonylat párja adja.
      const body =
        ownerId === null ? { invoiceId: chooser.invoice.id, none: true } : { invoiceId: ownerId };

      void api<void>('/tensoft/ledger-choices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(() => {
          markDecided(id);
          if (current.current !== id) return;
          setRechoosing(false);
          load(id);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            signOut('expired');
            return;
          }
          if (current.current !== id) return;
          setSaving(false);
          // A `409`: a kulcs közben egyértelművé vált. Újrapróbálni értelmetlen —
          // a friss állapotot kell megmutatni, és megmondani, miért változott.
          if (caught instanceof ApiError && caught.status === 409) {
            // A KULCS MÁR NEM KÉTES — a jelvény sem állhat rajta tovább.
            markDecided(id);
            setRechoosing(false);
            setError('conflict');
            load(id, true);
            return;
          }
          setError('failed');
        });
    },
    [documentId, chooser, saving, load, signOut, markDecided]
  );

  const pruneDecided = useCallback((since: number) => {
    setDecided((previous) => {
      const kept = [...previous].filter(([, at]) => at >= since);
      return kept.length === previous.size ? previous : new Map(kept);
    });
  }, []);

  /**
   * A `1`–`9` ÉS A `0` CSAK NYITOTT VÁLASZTÓNÁL ÉL. A `useShortcuts` minden
   * találatnál `preventDefault()`-ot hív — egy mindig bejegyzett számjegy
   * választó nélkül is elnyelné a leütést.
   */
  const shortcuts = useMemo<Shortcut[]>(() => {
    if (chooser === null || saving) return [];
    const keys: Shortcut[] = chooser.candidates.slice(0, KEYED_CANDIDATES).map((candidate, i) => ({
      key: String(i + 1),
      labelKey: 'shortcut.tensoftChoose',
      run: () => choose(candidate.id),
    }));
    keys.push({ key: '0', labelKey: 'shortcut.tensoftNone', run: () => choose(null) });
    return keys;
  }, [chooser, saving, choose]);

  return {
    status,
    view,
    chooser,
    rechoosing,
    startRechoose: () => {
      setError(null);
      setRechoosing(true);
    },
    cancelRechoose: () => setRechoosing(false),
    choose,
    saving,
    error,
    reload: () => (documentId === null ? undefined : load(documentId)),
    shortcuts,
    decided,
    pruneDecided,
  };
}
